/* ════════════════════════════════════════════════════
 *  <kik-global-map-stats> — hotspot markers <-> location cards.
 *
 *  Exactly one location is active at a time — the first by default.
 *  `is-active` on its `__pin` lights the marker dot and, on desktop,
 *  reveals that location's card.
 *
 *  Desktop: hovering, focusing or clicking a marker activates its location;
 *           the card is positioned to stay fully inside the map panel.
 *  Mobile:  tapping a marker slides the <kik-swiper> carousel to its card;
 *           the marker for the active slide stays active.
 * ════════════════════════════════════════════════════ */

if (!customElements.get('kik-global-map-stats')) {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class KikGlobalMapStats extends HTMLElement {
    connectedCallback() {
      this.markers = [...this.querySelectorAll('.section-kik-global-map-stats__marker')];
      if (this.markers.length === 0) return;

      this.pins = this.markers.map((marker) => marker.parentElement);
      this.panel = this.querySelector('.section-kik-global-map-stats__map');
      this.swiperHost = this.querySelector('kik-swiper');
      this.desktop = window.matchMedia('(min-width: 750px)');
      this.activeIndex = 0;

      this.onMarkerClick = this.onMarkerClick.bind(this);
      this.onMarkerPreview = this.onMarkerPreview.bind(this);
      this.syncActiveMarker = this.syncActiveMarker.bind(this);

      for (const marker of this.markers) {
        marker.addEventListener('click', this.onMarkerClick);
        marker.addEventListener('mouseenter', this.onMarkerPreview);
        marker.addEventListener('focus', this.onMarkerPreview);
      }

      // Re-place the active card when the map panel resizes — window resize,
      // map image load, or a breakpoint change.
      if (this.panel) {
        this.resizeObserver = new ResizeObserver(() => this.placeActiveCard());
        this.resizeObserver.observe(this.panel);
      }
      this.bindCarousel();
    }

    disconnectedCallback() {
      for (const marker of this.markers ?? []) {
        marker.removeEventListener('click', this.onMarkerClick);
        marker.removeEventListener('mouseenter', this.onMarkerPreview);
        marker.removeEventListener('focus', this.onMarkerPreview);
      }
      this.swiper?.off('slideChange', this.syncActiveMarker);
      this.resizeObserver?.disconnect();
    }

    /* Click — desktop activates the location; mobile slides the carousel. */
    onMarkerClick(event) {
      const index = this.markers.indexOf(event.currentTarget);
      if (index === -1) return;
      if (this.desktop.matches) {
        this.setActive(index);
      } else {
        this.swiperHost?.swiper?.slideTo(index);
      }
    }

    /* Hover / keyboard focus — desktop only — activates the location. */
    onMarkerPreview(event) {
      if (!this.desktop.matches) return;
      this.setActive(this.markers.indexOf(event.currentTarget));
    }

    /* <kik-swiper> builds its Swiper in its own connectedCallback, which can
       run after this one — retry on the next frame until it exists. */
    bindCarousel(attempt = 0) {
      if (!this.isConnected || !this.swiperHost) return;

      const swiper = this.swiperHost.swiper;
      if (!swiper) {
        if (attempt < 30) requestAnimationFrame(() => this.bindCarousel(attempt + 1));
        return;
      }
      this.swiper = swiper;
      swiper.on('slideChange', this.syncActiveMarker);
      this.syncActiveMarker();
    }

    syncActiveMarker() {
      this.setActive(this.swiper ? this.swiper.realIndex : 0);
    }

    /* Exactly one `__pin` carries `is-active`. */
    setActive(index) {
      if (index < 0 || index >= this.pins.length) return;
      this.activeIndex = index;
      for (const [i, pin] of this.pins.entries()) {
        pin.classList.toggle('is-active', i === index);
      }
      this.placeActiveCard();
    }

    /* Desktop — place the active card near its pin but fully inside the map
       panel: keep the preferred corner, flip it when that would overflow,
       clamp to the panel as a last resort. */
    placeActiveCard() {
      if (!this.desktop.matches) return;

      const pin = this.pins[this.activeIndex];
      const card = pin?.querySelector('.section-kik-global-map-stats__pin-card');
      const marker = pin?.querySelector('.section-kik-global-map-stats__marker');
      if (!card || !marker || !this.panel) return;

      const panel = this.panel.getBoundingClientRect();
      const mark = marker.getBoundingClientRect();
      const cardW = card.offsetWidth;
      const cardH = card.offsetHeight;
      if (panel.width === 0 || cardW === 0) return; // not visible yet

      const edge = 12; // safe margin from the panel edges
      const half = mark.width / 2;
      const pinX = mark.left + half - panel.left;
      const pinY = mark.top + half - panel.top;
      const prefer = pin.dataset.card || 'top-right';

      // Horizontal — card edge aligns with the marker edge, extending right/left.
      const xRight = pinX - half;
      const xLeft = pinX + half - cardW;
      let left = prefer.endsWith('left') ? xLeft : xRight;
      if (left < edge || left + cardW > panel.width - edge) {
        const alt = prefer.endsWith('left') ? xRight : xLeft;
        if (alt >= edge && alt + cardW <= panel.width - edge) left = alt;
      }
      left = clamp(left, edge, Math.max(edge, panel.width - cardW - edge));

      // Vertical — card sits above / below the marker.
      const yAbove = pinY - half - edge - cardH;
      const yBelow = pinY + half + edge;
      let top = prefer.startsWith('bottom') ? yBelow : yAbove;
      if (top < edge || top + cardH > panel.height - edge) {
        const alt = prefer.startsWith('bottom') ? yAbove : yBelow;
        if (alt >= edge && alt + cardH <= panel.height - edge) top = alt;
      }
      top = clamp(top, edge, Math.max(edge, panel.height - cardH - edge));

      // The card's containing block is the zero-size pin — offset from it.
      card.style.left = `${left - pinX}px`;
      card.style.top = `${top - pinY}px`;
      card.style.right = 'auto';
      card.style.bottom = 'auto';
    }
  }

  customElements.define('kik-global-map-stats', KikGlobalMapStats);
}
